/**
 * Reading, cleaning and filing clock swipes (Daily Time import) and the readiness checks that
 * come before an import or a generation. Pure functions: no store, no network, no clock.
 *
 * Time zones. Swipes, periods and schedules are PH wall-clock times kept as plain 'YYYY-MM-DD
 * HH:mm:ss' text. Everything here parses and does arithmetic on that text with dayjs.utc, so the
 * result does not depend on the browser's time zone (a zone with daylight saving would otherwise
 * move a swipe on the two nights a year the clock jumps). A swipe that carries an explicit offset
 * (a trailing Z or +hh:mm) is the one case that is converted: to Philippine time.
 */
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(utc)
dayjs.extend(customParseFormat)

export const LOG_FORMAT = 'YYYY-MM-DD HH:mm:ss'
const PH_OFFSET_MINUTES = 8 * 60
/** two swipes closer than this are one tap (a double press on the reader) */
export const DOUBLE_TAP_SECONDS = 60
/** logHours (util.ts) ignores a swipe within this many minutes of an earlier one */
export const LOG_RESOLUTION_MINUTES = 30

export type DateOrder = 'MDY' | 'DMY'

const pad = (n: number | string, width = 2) => String(n).padStart(width, '0')

/** A real calendar moment as canonical text, or null (Feb 30, hour 25, ...). */
function build(y: number, mo: number, d: number, h: number, mi: number, s: number): string | null {
  if (y < 2000 || y > 2100) return null
  const text = `${y}-${pad(mo)}-${pad(d)} ${pad(h)}:${pad(mi)}:${pad(s)}`
  const parsed = dayjs.utc(text, LOG_FORMAT, true)
  return parsed.isValid() ? text : null
}

/** An Excel serial date-time (days since 1899-12-30, the time as the fraction). */
function fromExcelSerial(n: number): string | null {
  if (!(n > 20000 && n < 80000)) return null
  const seconds = Math.round(n * 86400)
  const moment = dayjs.utc('1899-12-30 00:00:00', LOG_FORMAT, true).add(seconds, 'second')
  return moment.format(LOG_FORMAT)
}

/**
 * One swipe as 'YYYY-MM-DD HH:mm:ss', or null when it cannot be read with certainty.
 * Reads ISO ('2026-09-22 02:53', '2026-09-22T02:53:10', with Z or an offset), 'YYYY/MM/DD',
 * 'M/D/YYYY' or 'D/M/YYYY' (per `order`) with 24-hour or AM/PM time, two-digit years, and Excel
 * serial numbers. Anything else, and any impossible date or time, is null.
 */
export function parseSwipe(raw: unknown, order: DateOrder = 'MDY'): string | null {
  if (raw === null || raw === undefined) return null
  if (typeof raw === 'number') return fromExcelSerial(raw)
  let text = String(raw).replace(/^﻿/, '').replace(/^["'\s]+|["'\s]+$/g, '').replace(/\s+/g, ' ')
  if (text === '') return null

  if (/^\d+(\.\d+)?$/.test(text)) return fromExcelSerial(Number(text))

  // ISO-like: 2026-09-22 02:53[:10][.123] [Z|+08:00]
  let m = text.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?\s*(Z|[+-]\d{2}:?\d{2})?$/i)
  if (m) {
    const [, y, mo, d, h = '0', mi = '0', s = '0', zone] = m
    const local = build(+y, +mo, +d, +h, +mi, +s)
    if (!local || m[4] === undefined) return null // an impossible date, or a date with no time: not a swipe
    if (!zone) return local
    if (zone.toUpperCase() === 'Z') return dayjs.utc(local, LOG_FORMAT).add(PH_OFFSET_MINUTES, 'minute').format(LOG_FORMAT)
    const sign = zone[0] === '-' ? -1 : 1
    const digits = zone.slice(1).replace(':', '')
    const offset = sign * (Number(digits.slice(0, 2)) * 60 + Number(digits.slice(2, 4)))
    return dayjs.utc(local, LOG_FORMAT).subtract(offset, 'minute').add(PH_OFFSET_MINUTES, 'minute').format(LOG_FORMAT)
  }

  // 9/22/2026 2:53[:10] [AM|PM]   (or 22/9/2026 when order is DMY)
  m = text.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4}) (\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AP]M)?$/i)
  if (m) {
    const [, a, b, yy, hh, mi, s = '0', meridiem] = m
    const year = yy.length === 2 ? 2000 + +yy : +yy
    const [month, day] = order === 'MDY' ? [+a, +b] : [+b, +a]
    let hour = +hh
    if (meridiem) {
      if (hour < 1 || hour > 12) return null
      hour = (hour % 12) + (meridiem.toUpperCase() === 'PM' ? 12 : 0)
    }
    return build(year, month, day, hour, +mi, +s)
  }
  return null
}

/**
 * Which day-first/month-first order a file uses. A first number above 12 can only be a day
 * (D/M/Y); a second number above 12 can only be a day (M/D/Y). The spreadsheets this app is fed
 * export M/D/Y, so that is also the answer when nothing in the file can tell.
 */
export function detectDateOrder(values: unknown[]): DateOrder {
  let dmy = 0
  let mdy = 0
  for (const value of values) {
    const m = String(value ?? '').trim().match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})\b/)
    if (!m) continue
    if (+m[1] > 12) dmy++
    else if (+m[2] > 12) mdy++
  }
  return dmy > mdy ? 'DMY' : 'MDY'
}

/**
 * An employee number as the key two spellings of it share: '00107', ' 107 ' and '107' are the same
 * person; an alphanumeric id ('E-017') is compared as upper-case text.
 */
export function normalizeId(raw: unknown): string {
  const text = String(raw ?? '').replace(/^["'\s]+|["'\s]+$/g, '')
  if (/^\d+$/.test(text)) return text.replace(/^0+(?=\d)/, '')
  return text.toUpperCase()
}

// ---------------------------------------------------------------- schedules and shifts

export interface ShiftWindow {
  /** minutes after midnight the shift starts */
  startMinutes: number
  /** length of the shift in minutes, break included (a shift ending before it starts runs overnight) */
  durationMinutes: number
  overnight: boolean
}

/** 'HH:mm' of a schedule time, read as the wall-clock time it is stored as (see the header). */
export function scheduleClock(value: unknown): { h: number; m: number } | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = dayjs.utc(value as any)
  return parsed.isValid() ? { h: parsed.hour(), m: parsed.minute() } : null
}

/** The shift a schedule describes, or null when its start or end time is missing or unreadable. */
export function shiftWindow(schedule: { time_in?: unknown; time_out?: unknown } | null | undefined): ShiftWindow | null {
  if (!schedule) return null
  const start = scheduleClock(schedule.time_in)
  const end = scheduleClock(schedule.time_out)
  if (!start || !end) return null
  const startMinutes = start.h * 60 + start.m
  let duration = end.h * 60 + end.m - startMinutes
  const overnight = duration <= 0
  if (overnight) duration += 24 * 60
  return { startMinutes, durationMinutes: duration, overnight }
}

const WINDOW_PADDING_MINUTES = 4 * 60

/**
 * The calendar day ('YYYYMMDD') a swipe belongs to. Without a shift it is the swipe's own date.
 * With one, a swipe belongs to the day whose shift it falls in (from 4 hours before the shift
 * starts to 4 hours after it ends), so the 06:02 clock-out of a 22:00 night shift is filed under
 * the evening it started, not the morning it happened.
 */
export function shiftDateKey(swipe: string, shift: ShiftWindow | null): string {
  const at = dayjs.utc(swipe, LOG_FORMAT, true)
  const own = at.format('YYYYMMDD')
  if (!shift) return own
  let best: { key: string; distance: number } | null = null
  for (const back of [0, 1]) {
    const day = at.startOf('day').subtract(back, 'day')
    const start = day.add(shift.startMinutes, 'minute')
    const end = start.add(shift.durationMinutes, 'minute')
    const from = start.subtract(WINDOW_PADDING_MINUTES, 'minute')
    const to = end.add(WINDOW_PADDING_MINUTES, 'minute')
    if (at.isBefore(from) || at.isAfter(to)) continue
    const centre = start.add(shift.durationMinutes / 2, 'minute')
    const distance = Math.abs(at.diff(centre, 'minute'))
    if (!best || distance < best.distance) best = { key: day.format('YYYYMMDD'), distance }
  }
  return best ? best.key : own
}

// ---------------------------------------------------------------- log text

const splitLogs = (logs: string | undefined | null) =>
  String(logs ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '')

/** Sorted, unique canonical swipes; anything that cannot be read is dropped and counted. */
export function normalizeLogs(
  logs: string | string[] | undefined | null,
  order: DateOrder = 'MDY'
): { swipes: string[]; unreadable: string[] } {
  const parts = Array.isArray(logs) ? logs : splitLogs(logs)
  const unreadable: string[] = []
  const seen = new Set<string>()
  for (const part of parts) {
    const parsed = parseSwipe(part, order)
    if (parsed) seen.add(parsed)
    else unreadable.push(String(part))
  }
  const swipes = [...seen].sort()
  // a second tap within a minute of the one before is the same press
  const tapped = swipes.filter((s, i) => i === 0 || dayjs.utc(s, LOG_FORMAT).diff(dayjs.utc(swipes[i - 1], LOG_FORMAT), 'second') >= DOUBLE_TAP_SECONDS)
  return { swipes: tapped, unreadable }
}

/** The comma-separated text a daily time stores (no trailing separator). */
export const joinLogs = (swipes: string[]) => swipes.join(', ')

/** Existing logs plus new swipes, as one clean list: re-importing a file changes nothing. */
export function mergeLogs(existing: string | undefined | null, incoming: string[]): string {
  return joinLogs(normalizeLogs([...splitLogs(existing), ...incoming]).swipes)
}

/**
 * How many swipes the pay calculation will actually use: logHours (util.ts) ignores any swipe less
 * than 30 minutes after one before it. An odd count means a clock-in or a clock-out is missing and
 * the day would be worth no hours at all.
 */
export function usableSwipeCount(swipes: string[]): number {
  let kept = 0
  let last: dayjs.Dayjs | null = null
  for (const swipe of [...swipes].sort()) {
    const at = dayjs.utc(swipe, LOG_FORMAT)
    if (last && at.diff(last, 'minute') < LOG_RESOLUTION_MINUTES) continue
    kept++
    last = at
  }
  return kept
}

export const isIncomplete = (swipes: string[]) => swipes.length > 0 && usableSwipeCount(swipes) % 2 === 1

// ---------------------------------------------------------------- preparing a file

export interface RawSwipeRow {
  Id?: unknown
  DateTime?: unknown
}

export interface Swipe {
  /** 1-based row of the file, for messages */
  row: number
  id: string
  /** the id as the file wrote it */
  rawId: string
  at: string
}

export interface SkippedRow {
  row: number
  reason: string
}

const isHeaderRow = (row: RawSwipeRow) => /^(id|emp|employee)/i.test(String(row.Id ?? '').trim()) && /time|date/i.test(String(row.DateTime ?? ''))

/**
 * The swipes of a file, read once. Blank rows and a header row are dropped without comment; a row
 * with no employee number or a date and time that cannot be read is reported, with its row number.
 */
export function readSwipes(rows: RawSwipeRow[], order?: DateOrder) {
  const chosen = order ?? detectDateOrder(rows.map((r) => r?.DateTime))
  const swipes: Swipe[] = []
  const skipped: SkippedRow[] = []
  rows.forEach((row, index) => {
    const number = index + 1
    const idText = String(row?.Id ?? '').trim()
    const stamp = row?.DateTime
    if (idText === '' && (stamp === undefined || stamp === null || String(stamp).trim() === '')) return
    if (index === 0 && isHeaderRow(row)) return
    if (idText === '') {
      skipped.push({ row: number, reason: 'no employee number' })
      return
    }
    const at = parseSwipe(stamp, chosen)
    if (!at) {
      skipped.push({ row: number, reason: `date and time not readable: "${String(stamp ?? '').trim().slice(0, 40)}"` })
      return
    }
    swipes.push({ row: number, id: normalizeId(idText), rawId: idText, at })
  })
  return { swipes, skipped, order: chosen }
}

// ---------------------------------------------------------------- readiness

export interface Issue {
  code: string
  message: string
}

const WORK_DAY_CODES = ['Su', 'M', 'T', 'W', 'Th', 'F', 'S']

export interface ReadinessInput {
  period?: { uuid?: string; start_date?: unknown; end_date?: unknown; status?: string } | null
  employee?: { uuid?: string; employee_id?: string; company_uuid?: string | null } | null
  payslip?: { uuid?: string; schedule_uuid?: string | null; company_uuid?: string | null } | null
  schedule?: {
    time_in?: unknown
    time_out?: unknown
    time_break?: unknown
    time_resume?: unknown
    with_breaks?: boolean
    day_hours?: number
    work_days?: string
  } | null
  /** the company is a required part of a daily time only when the Company feature is on */
  companyEnabled?: boolean
  company_uuid?: string | null
  /** an import changes a period that must still be Open */
  requireOpen?: boolean
}

/** What is missing or wrong before daily times can be imported or generated for this employee. */
export function readiness(input: ReadinessInput): Issue[] {
  const issues: Issue[] = []
  const { period, employee, payslip, schedule } = input
  const add = (code: string, message: string) => issues.push({ code, message })

  if (!period?.uuid) add('no-period', 'No pay period is selected.')
  else {
    const start = period.start_date ? dayjs.utc(period.start_date as any) : null
    const end = period.end_date ? dayjs.utc(period.end_date as any) : null
    if (!start?.isValid() || !end?.isValid()) add('bad-period', 'The pay period has no valid start or end date.')
    else if (end.isBefore(start)) add('bad-period', 'The pay period ends before it starts.')
    if (input.requireOpen && period.status && period.status !== 'Open') {
      add('period-closed', `The pay period is ${period.status}; daily times can only be changed while it is Open.`)
    }
  }

  if (employee !== undefined) {
    if (!employee) add('no-employee', 'The employee was not found.')
    else if (!String(employee.employee_id ?? '').trim()) add('no-employee-id', 'The employee has no employee number.')
  }

  if (payslip !== undefined && !payslip) add('no-payslip', 'The employee has no payslip in this period. Generate payslips first.')

  if (payslip && !payslip.schedule_uuid && !schedule) add('no-schedule', 'The payslip has no shift (schedule) set.')
  else if (payslip && payslip.schedule_uuid && !schedule) add('schedule-missing', 'The shift (schedule) on the payslip no longer exists.')

  if (schedule) {
    const window = shiftWindow(schedule)
    if (!window) add('bad-shift', 'The shift has no readable start or end time.')
    else if (schedule.with_breaks && (!scheduleClock(schedule.time_break) || !scheduleClock(schedule.time_resume))) {
      add('bad-shift', 'The shift is set to have breaks but has no break or resume time.')
    }
    if (!(Number(schedule.day_hours) > 0)) add('no-day-hours', 'The shift has no regular hours per day.')
    const codes = String(schedule.work_days ?? '').split(',').map((d) => d.trim()).filter((d) => d !== '')
    const unknown = codes.filter((d) => !WORK_DAY_CODES.includes(d))
    if (unknown.length) add('bad-work-days', `The shift has unknown work days: ${unknown.join(', ')}.`)
  }

  if (input.companyEnabled && !(input.company_uuid || payslip?.company_uuid || employee?.company_uuid)) {
    add('no-company', 'The Company feature is on but no company is chosen for this employee.')
  }
  return issues
}

/** The calendar days ('YYYYMMDD') from the start to the end date of a period, both included. */
export function periodDays(period: { start_date?: unknown; end_date?: unknown }): string[] {
  if (!period?.start_date || !period?.end_date) return [] // dayjs.utc(undefined) is "now": never read a missing date
  const start = dayjs.utc(period.start_date as any).startOf('day')
  const end = dayjs.utc(period.end_date as any).startOf('day')
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) return []
  const days: string[] = []
  for (let d = start; !d.isAfter(end); d = d.add(1, 'day')) days.push(d.format('YYYYMMDD'))
  return days
}

/** The default two swipes (clock-in, clock-out) of a generated day, or null without a readable shift. */
export function generatedLogs(dateKey: string, schedule: { time_in?: unknown; time_out?: unknown }): string | null {
  const start = scheduleClock(schedule.time_in)
  const end = scheduleClock(schedule.time_out)
  if (!start || !end) return null
  const day = dayjs.utc(dateKey, 'YYYYMMDD', true)
  if (!day.isValid()) return null
  const clockIn = day.hour(start.h).minute(start.m).second(0)
  let clockOut = day.hour(end.h).minute(end.m).second(0)
  if (!clockOut.isAfter(clockIn)) clockOut = clockOut.add(1, 'day') // an overnight shift ends the next day
  return joinLogs([clockIn.format(LOG_FORMAT), clockOut.format(LOG_FORMAT)]).replace(', ', ',')
}
